import type { CredentialApplication, Token, AuthStorage } from './auth_types';
import { detectInstanceSoftware } from './instance_detection';

const SCOPES = 'read write follow push';
const CLIENT_NAME = 'Masto-FE Standalone';

function getRedirectUri(): string {
  return window.location.origin + window.location.pathname;
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export async function registerApp(
  domain: string,
): Promise<CredentialApplication> {
  const response = await fetch(`https://${domain}/api/v1/apps`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      client_name: CLIENT_NAME,
      redirect_uris: getRedirectUri(),
      scopes: SCOPES,
      website: window.location.origin,
    }),
  });

  if (!response.ok) {
    const error = (await response.json()) as { error?: string };
    throw new Error(error.error ?? 'Failed to register app');
  }

  return (await response.json()) as CredentialApplication;
}

export async function authorize(
  domain: string,
  clientId: string,
): Promise<void> {
  const software = await detectInstanceSoftware(`https://${domain}`);
  const usePKCE = software === 'mastodon';

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: getRedirectUri(),
    scope: SCOPES,
  });

  if (usePKCE) {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    params.append('code_challenge', codeChallenge);
    params.append('code_challenge_method', 'S256');

    localStorage.setItem('code_verifier', codeVerifier);
  }

  window.location.href = `https://${domain}/oauth/authorize?${params.toString()}`;
}

export async function getToken(
  domain: string,
  code: string,
  clientId: string,
  clientSecret: string,
): Promise<Token> {
  const codeVerifier = localStorage.getItem('code_verifier');

  const body: Record<string, string> = {
    grant_type: 'authorization_code',
    code,
    client_id: clientId,
    client_secret: clientSecret,
    redirect_uri: getRedirectUri(),
    scope: SCOPES,
  };

  if (codeVerifier) {
    body.code_verifier = codeVerifier;
    localStorage.removeItem('code_verifier');
  }

  const response = await fetch(`https://${domain}/oauth/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(body),
  });

  if (!response.ok) {
    const error = (await response.json()) as {
      error_description?: string;
      error?: string;
    };
    throw new Error(
      error.error_description ?? error.error ?? 'Failed to obtain token',
    );
  }

  return (await response.json()) as Token;
}

export function saveAuthData(data: Partial<AuthStorage>): void {
  const current = loadAuthData() ?? ({} as AuthStorage);
  const updated = { ...current, ...data };

  if (updated.domain) localStorage.setItem('domain', updated.domain);
  if (updated.client_id) localStorage.setItem('client_id', updated.client_id);
  if (updated.client_secret)
    localStorage.setItem('client_secret', updated.client_secret);
  if (updated.access_token)
    localStorage.setItem('access_token', updated.access_token);
}

export function loadAuthData(): AuthStorage | null {
  const domain = localStorage.getItem('domain');
  const client_id = localStorage.getItem('client_id');
  const client_secret = localStorage.getItem('client_secret');
  const access_token = localStorage.getItem('access_token');

  if (!domain) return null;

  return {
    domain,
    client_id: client_id ?? '',
    client_secret: client_secret ?? '',
    access_token: access_token ?? undefined,
  };
}

export function clearAuthData(): void {
  localStorage.removeItem('domain');
  localStorage.removeItem('client_id');
  localStorage.removeItem('client_secret');
  localStorage.removeItem('access_token');
  localStorage.removeItem('code_verifier');
  localStorage.removeItem('initial_state');
}

// Standalone-mode discriminator shared across the app (api.ts, router.tsx, …).
// We are in standalone mode when an OAuth session has been stored locally.
export function isStandalone(): boolean {
  try {
    return !!localStorage.getItem('access_token');
  } catch {
    return false;
  }
}

// Base URL for API requests. In standalone mode requests target the user's
// instance; otherwise an empty string keeps the upstream relative-path behaviour.
export function getApiBaseUrl(): string {
  try {
    const domain = localStorage.getItem('domain');
    if (isStandalone() && domain) {
      return `https://${domain}`;
    }
  } catch {
    // ignore
  }
  return '';
}
